function xrefRollbar(context, err, extra) {
  if (!window.Rollbar) return;
  // Aborted fetches (navigation, superseded searches) are expected, not errors.
  if (
    err &&
    typeof err === 'object' &&
    (err.name === 'AbortError' || /\babort(ed)?\b/i.test(String(err.message || '')))
  ) {
    return;
  }
  try {
    const message =
      err && typeof err === 'object' && err.message
        ? String(err.message)
        : typeof err === 'string'
          ? err
          : 'CrossReference error';
    Rollbar.error(message, err instanceof Error ? err : { detail: err }, {
      context: 'CrossReference.' + context,
      ...(extra && typeof extra === 'object' ? extra : {}),
    });
  } catch (_) {}
}

function wrapCrossReferencePrototype(Ctor, className) {
  if (!Ctor || !Ctor.prototype) return;
  const proto = Ctor.prototype;
  Object.getOwnPropertyNames(proto).forEach((name) => {
    if (name === 'constructor') return;
    const desc = Object.getOwnPropertyDescriptor(proto, name);
    if (!desc || typeof desc.value !== 'function') return;
    const original = desc.value;
    proto[name] = function xrefWrapped(...args) {
      try {
        const result = original.apply(this, args);
        if (result != null && typeof result.then === 'function') {
          return result.catch((e) => {
            xrefRollbar(`${className}.${name}`, e);
            return Promise.reject(e);
          });
        }
        return result;
      } catch (e) {
        xrefRollbar(`${className}.${name}`, e);
        throw e;
      }
    };
  });
}

class CrossReferencePage extends HTMLElement {
    constructor() {
        super();
        try {
          this.selectElement = null;
          this.productsListing = null;
          this.sectionId = null;
          this.optionsBound = false;
          this.isLoading = false;
          this.currentCollectionHandle = null;
          this.allProducts = [];
          this.currentSearchTerm = '';
          this.collectionCardsCache = new Map();
          this.activeSearchAbort = null;
          this.searchRequestId = 0;
        } catch (e) {
          xrefRollbar('CrossReferencePage.constructor', e);
        }
    }

    connectedCallback() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }

    init() {
        this.selectElement = this.querySelector('[data-cross-reference-select]');
        this.productsListing = document.getElementById('products-listing');
        
        // Get sectionId from child section element (since we're now an autonomous custom element)
        const sectionElement = this.querySelector('section[data-section-id]');
        this.sectionId = sectionElement ? sectionElement.dataset.sectionId : this.dataset.sectionId;
        
        if (!this.selectElement || !this.productsListing) {
            return;
        }
        
        // Use requestAnimationFrame for Safari compatibility to ensure DOM is ready
        requestAnimationFrame(() => {
            this.populateCollectionsDropdown();
            this.bindEvents();
            this.prefetchCollectionCards();
        });
    }

    populateCollectionsDropdown(retryCount = 0) {
        const collectionsScript = this.querySelector('[data-collections-json]');
        
        // Safari-compatible element querying - try multiple approaches
        let collectionsList = null;
        if (this.selectElement) {
            // Try querying within selectElement first
            collectionsList = this.selectElement.querySelector('[data-collections-list]');
        }
        
        // Fallback: query from the custom element itself
        if (!collectionsList) {
            collectionsList = this.querySelector('[data-collections-list]');
        }
        
        // Fallback: query from document (for Safari compatibility)
        if (!collectionsList) {
            collectionsList = document.querySelector('[data-collections-list]');
        }
        
        // Safari retry mechanism - retry up to 3 times with small delay
        if (!collectionsList && retryCount < 3) {
            setTimeout(() => {
                this.populateCollectionsDropdown(retryCount + 1);
            }, 100);
            return;
        }
        
        if (!collectionsScript || !collectionsList) {
            return;
        }
        
        try {
            const jsonText = collectionsScript.textContent.trim();
            
            if (!jsonText || jsonText === '{}') {
                return;
            }
            
            const collectionsObj = JSON.parse(jsonText);
            
            if (!collectionsObj || typeof collectionsObj !== 'object' || Array.isArray(collectionsObj)) {
                return;
            }
            
            const collectionsArray = Object.values(collectionsObj);
            
            if (collectionsArray.length === 0) {
                return;
            }
            
            collectionsList.innerHTML = '';
            
            collectionsArray.forEach((collection, index) => {
                if (!collection || !collection.handle || !collection.title) {
                    return;
                }
                const optionId = `manufacturer-option-${index + 1}`;
                const li = document.createElement('li');
                li.setAttribute('data-value', collection.handle);
                li.className = 'form-check';
                li.setAttribute('role', 'menuitem');
                li.setAttribute('tabindex', '');
                
                const input = document.createElement('input');
                input.type = 'radio';
                input.value = collection.handle;
                input.className = 'sortby_options form-check-input';
                input.name = 'manufacturer';
                input.id = optionId;
                input.setAttribute('tabindex', '');
                
                const label = document.createElement('label');
                label.setAttribute('for', optionId);
                label.className = 'option_txt form-check-label';
                label.setAttribute('title', collection.title);
                label.textContent = collection.title;
                
                li.appendChild(input);
                li.appendChild(label);
                collectionsList.appendChild(li);
            });
        } catch (error) {
            xrefRollbar('populateCollectionsDropdown.parse', error);
        }
    }

    bindEvents() {
        if (this.optionsBound) {
            return;
        }
        this.optionsBound = true;

        const handleSelection = (collectionHandle) => {
            if (!collectionHandle || collectionHandle === 'Select Manufacturer') return;
            
            if (this.isLoading && this.currentCollectionHandle === collectionHandle) {
                return;
            }
            
            if (this.selectElement && this.selectElement.classList.contains('open')) {
                this.selectElement.closeDropdown();
            }
            
            // Clear search bar input when collection is selected
            const searchInput = document.getElementById('serial-number-input');
            if (searchInput) {
                searchInput.value = '';
            }
            
            // Remove selected class from all options
            const allOptions = this.selectElement.querySelectorAll('li[data-value]');
            allOptions.forEach(option => {
                option.classList.remove('selected');
                const radio = option.querySelector('input[type="radio"]');
                if (radio) {
                    radio.checked = false;
                }
            });
            
            // Add selected class to the selected option and check its radio button
            const selectedOption = this.selectElement.querySelector(`li[data-value="${collectionHandle}"]`);
            if (selectedOption) {
                selectedOption.classList.add('selected');
                const radio = selectedOption.querySelector('input[type="radio"]');
                if (radio) {
                    radio.checked = true;
                }
                
                // Update toggle button text to show selected manufacturer name
                const toggleButton = this.selectElement.querySelector('button[data-toggle="options"]');
                const optionText = this.selectElement.querySelector('.option_txt');
                const label = selectedOption.querySelector('label');
                
                if (toggleButton && optionText && label) {
                    optionText.textContent = label.textContent.trim();
                }
            }
            
            this.loadCollectionProducts(collectionHandle);
        };

        this.selectElement.addEventListener('change', (e) => {
            const selectedValue = e.detail?.value || this.getSelectedValue();
            handleSelection(selectedValue);
        });

        const options = this.selectElement.querySelectorAll('li[data-value]');
        options.forEach(option => {
            option.addEventListener('click', (e) => {
                e.stopPropagation();
                const collectionHandle = option.getAttribute('data-value');
                clearTimeout(option._selectionTimeout);
                option._selectionTimeout = setTimeout(() => {
                    handleSelection(collectionHandle);
                }, 200);
            });
        });

        const clearSearchBtn = this.querySelector('[data-clear-search]');
        if (clearSearchBtn) {
            clearSearchBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearSearch();
            });
        }

        const searchInput = document.getElementById('serial-number-input');
        const searchProductsBtn = this.querySelector('[data-search-products]') || document.querySelector('[data-search-products]');
        
        if (searchProductsBtn) {
            // Enable button by default on page load
            searchProductsBtn.disabled = false;
            
            if (searchInput) {
                searchInput.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.keyCode === 13) {
                        e.preventDefault();
                        this.handleSearchClick(searchInput);
                    }
                });
            }
            
            searchProductsBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleSearchClick(searchInput);
            });
        }
    }

    handleSearchClick(searchInput) {
        if (!searchInput || searchInput.value.trim().length === 0) {
            if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
                window.notificationEle.updateNotification('Input Required', 'Type OEM or KELTEC Part number.', { type: 'error', timeout: 3000 });
            }
            if (searchInput) {
                searchInput.focus();
            }
            return;
        }
        this.performPredictiveSearch();
    }

    getSelectedValue() {
        const selectedOption = this.selectElement.querySelector('li.selected');
        if (selectedOption) {
            return selectedOption.getAttribute('data-value');
        }
        return null;
    }

    getManufacturerHandles() {
        const collectionsScript = this.querySelector('[data-collections-json]');
        if (!collectionsScript) return [];

        try {
            const collectionsObj = JSON.parse(collectionsScript.textContent.trim());
            return Object.values(collectionsObj)
                .map((collection) => collection?.handle)
                .filter(Boolean);
        } catch (error) {
            xrefRollbar('getManufacturerHandles.parse', error);
            return [];
        }
    }

    abortActiveSearch() {
        if (this.activeSearchAbort) {
            this.activeSearchAbort.abort();
            this.activeSearchAbort = null;
        }
    }

    stripHtmlFromCell(html) {
        return String(html || '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    extractTableCellTexts(descriptionHtml) {
        const rows = [];
        const rowPattern = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
        let rowMatch;

        while ((rowMatch = rowPattern.exec(descriptionHtml)) !== null) {
            if (/class="[^"]*view-more/i.test(rowMatch[0])) continue;

            const cells = [];
            const cellPattern = /<td[^>]*>([\s\S]*?)<\/td>/gi;
            let cellMatch;

            while ((cellMatch = cellPattern.exec(rowMatch[1])) !== null) {
                cells.push(this.stripHtmlFromCell(cellMatch[1]));
            }

            if (cells.length) rows.push(cells);
        }

        return rows;
    }

    prefetchCollectionCards() {
        const handles = this.getManufacturerHandles();
        if (!handles.length) return;

        const warmCache = () => {
            let index = 0;
            const concurrency = 3;

            const loadNext = () => {
                if (index >= handles.length) return;
                const batch = handles.slice(index, index + concurrency);
                index += concurrency;
                Promise.all(
                    batch.map((handle) =>
                        this.fetchAllCollectionProductCards(handle).catch(() => [])
                    )
                ).finally(loadNext);
            };

            loadNext();
        };

        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(warmCache, { timeout: 4000 });
        } else {
            setTimeout(warmCache, 1500);
        }
    }

    normalizePartNumber(value) {
        return String(value || '').toLowerCase().replace(/[\s\-_./\\#,]/g, '');
    }

    partNumbersEqual(a, b) {
        const normA = this.normalizePartNumber(a);
        const normB = this.normalizePartNumber(b);
        return Boolean(normA && normB && normA === normB);
    }

    cellMatchesPartNumber(cellText, searchTerm) {
        const normTerm = this.normalizePartNumber(searchTerm);
        if (!normTerm || !cellText) return false;

        const trimmed = String(cellText).trim();
        if (this.normalizePartNumber(trimmed) === normTerm) return true;

        return trimmed
            .split(/[\s,;/|]+/)
            .some((token) => this.normalizePartNumber(token) === normTerm);
    }

    getProductDescriptionHtml(element) {
        if (!element) return '';

        const card =
            element.matches('[data-product-card]') ? element : element.querySelector('[data-product-card]');

        return (
            element.getAttribute('data-product-description') ||
            card?.getAttribute('data-product-description') ||
            element.querySelector('[data-product-description]')?.getAttribute('data-product-description') ||
            ''
        );
    }

    getProductTitle(element) {
        if (!element) return '';

        const card =
            element.matches('[data-product-card]') ? element : element.querySelector('[data-product-card]');

        return (
            element.getAttribute('data-title') ||
            card?.getAttribute('data-title') ||
            ''
        ).trim();
    }

    productTitleMatchesSearch(title, searchTerm) {
        const term = (searchTerm || '').trim();
        if (!title || !term) return false;

        const titleLower = title.toLowerCase();
        const termLower = term.toLowerCase();

        if (titleLower.includes(termLower)) return true;
        return this.partNumbersEqual(title, term);
    }

    findOemTableRow(descriptionHtml, searchTerm) {
        if (!descriptionHtml) return null;

        const rows = this.extractTableCellTexts(descriptionHtml);
        if (!rows.length) return null;

        const term = (searchTerm || '').trim();

        if (term) {
            for (const cellValues of rows) {
                const manufacturer = cellValues[0] || '';
                const oem = cellValues[1] || '';

                if (cellValues.some((value) => this.cellMatchesPartNumber(value, term))) {
                    const matchedCell =
                        cellValues.find((value) => this.cellMatchesPartNumber(value, term)) || oem;
                    return { manufacturer, oem: matchedCell };
                }
            }
            return null;
        }

        if (rows[0].length < 2) return null;

        return {
            manufacturer: rows[0][0] || '',
            oem: rows[0][1] || '',
        };
    }

    descriptionContainsOemSearch(descriptionHtml, searchTerm) {
        const term = (searchTerm || '').trim();
        if (!descriptionHtml || !term) return false;

        return Boolean(this.findOemTableRow(descriptionHtml, term));
    }

    productMatchesOemDescriptionSearch(cardElement, searchTerm) {
        if (this.productTitleMatchesSearch(this.getProductTitle(cardElement), searchTerm)) {
            return true;
        }

        return this.descriptionContainsOemSearch(
            this.getProductDescriptionHtml(cardElement),
            searchTerm
        );
    }

    wrapProductCardHtml(card) {
        const colWrapper = card.closest('[data-cross-reference-product-wrapper]');
        if (colWrapper) {
            const clone = colWrapper.cloneNode(true);
            clone.classList.remove('col-lg-4', 'col-lg-6', 'col-lg-2', 'col-xl-3');
            clone.classList.add('col-lg-3');
            return clone.outerHTML;
        }

        return `<div class="col-lg-3 col-md-4 col-6 px-md-3 px-2 d-flex align-items-stretch justify-content-around" data-cross-reference-product-wrapper">${card.outerHTML}</div>`;
    }

    filterCardsByOemDescription(cards, searchTerm, seenHandles = new Set()) {
        let productsHTML = '';
        let matchCount = 0;

        cards.forEach((card) => {
            const handle = card.getAttribute('data-product-handle');
            if (handle && seenHandles.has(handle)) return;
            if (!this.productMatchesOemDescriptionSearch(card, searchTerm)) return;
            if (handle) seenHandles.add(handle);

            productsHTML += this.wrapProductCardHtml(card);
            matchCount++;
        });

        return { productsHTML, matchCount, seenHandles };
    }

    getMaxPageFromDoc(doc, currentPage = 1) {
        let maxPage = currentPage;

        doc.querySelectorAll('.pagination a[href*="page="], [data-pagelinks]').forEach((link) => {
            const href = link.getAttribute('href') || '';
            const match = href.match(/[?&]page=(\d+)/);
            if (match) {
                maxPage = Math.max(maxPage, parseInt(match[1], 10));
            }
        });

        return maxPage;
    }

    parseCollectionPageCards(html, parser = new DOMParser()) {
        const doc = parser.parseFromString(html, 'text/html');
        return {
            doc,
            cards: Array.from(doc.querySelectorAll('[data-product-card]')),
        };
    }

    cardsFromCachedHtml(cachedHtml, parser = new DOMParser()) {
        return cachedHtml.map((html) => {
            const doc = parser.parseFromString(html, 'text/html');
            return doc.querySelector('[data-product-card]');
        }).filter(Boolean);
    }

    async fetchCollectionPageCards(collectionHandle, page, signal) {
        const url = `/collections/${collectionHandle}?view=filtercross&page=${page}`;
        const response = await fetch(url, { signal });
        if (!response.ok) return [];

        const html = await response.text();
        return this.parseCollectionPageCards(html).cards;
    }

    async fetchAllCollectionProductCards(collectionHandle, { signal, onPage } = {}) {
        if (this.collectionCardsCache.has(collectionHandle)) {
            const cachedCards = this.cardsFromCachedHtml(this.collectionCardsCache.get(collectionHandle));
            if (onPage) onPage(cachedCards);
            return cachedCards;
        }

        const parser = new DOMParser();
        const url = `/collections/${collectionHandle}?view=filtercross&page=1`;
        const response = await fetch(url, { signal });
        if (!response.ok) return [];

        const html = await response.text();
        const { doc, cards: page1Cards } = this.parseCollectionPageCards(html, parser);
        if (!page1Cards.length) return [];

        if (onPage) onPage(page1Cards);

        const maxPage = Math.min(this.getMaxPageFromDoc(doc, 1), 25);
        let allCards = page1Cards;

        if (maxPage > 1) {
            const pageNumbers = Array.from({ length: maxPage - 1 }, (_, index) => index + 2);
            // Fetch in small batches: firing every page at once (times several collections when
            // prefetching) trips Shopify's rate limit (HTTP 429).
            const pageBatchSize = 4;
            for (let i = 0; i < pageNumbers.length; i += pageBatchSize) {
                if (signal?.aborted) break;
                const pageResults = await Promise.all(
                    pageNumbers
                        .slice(i, i + pageBatchSize)
                        .map((page) =>
                            this.fetchCollectionPageCards(collectionHandle, page, signal).catch(() => [])
                        )
                );
                pageResults.forEach((cards) => {
                    if (!cards.length) return;
                    allCards = allCards.concat(cards);
                    if (onPage) onPage(cards);
                });
            }
        }

        this.collectionCardsCache.set(
            collectionHandle,
            allCards.map((card) => card.outerHTML)
        );

        return this.cardsFromCachedHtml(this.collectionCardsCache.get(collectionHandle), parser);
    }

    extractSearchProductCards(doc) {
        const searchSection = doc.querySelector('#template-search');
        if (!searchSection) return [];

        return Array.from(searchSection.children).filter((card) => {
            const isProductWrapper = card.hasAttribute('data-cross-reference-product-wrapper');
            const hasProductLink = card.querySelector('a[href*="/products/"]');
            const notPagination =
                !card.closest('[data-pagination-parent]') &&
                !card.closest('.pagination-wrapper') &&
                !card.closest('[data-data-paginationParent]') &&
                !card.hasAttribute('data-data-paginationParent') &&
                !card.hasAttribute('data-pagination-parent');
            return isProductWrapper && hasProductLink && notPagination;
        });
    }

    async tryShopifyOemSearch(searchTerm, signal) {
        const searchUrl = `/search?q=${encodeURIComponent(searchTerm)}&view=cross-reference&limit=48`;
        const response = await fetch(searchUrl, { signal });
        if (!response.ok) return null;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrappers = this.extractSearchProductCards(doc);
        if (!wrappers.length) return null;

        const cards = wrappers
            .map((wrapper) => wrapper.querySelector('[data-product-card]') || wrapper)
            .filter(Boolean);

        const filtered = this.filterCardsByOemDescription(cards, searchTerm);
        return filtered.matchCount > 0 ? filtered : null;
    }

    renderOemSearchResults(productsHTML, matchCount, collectionUrl = '') {
        this.allProducts = this.parseProductsFromHTML(productsHTML);
        this.renderProducts(productsHTML, matchCount, collectionUrl, '');
        this.isLoading = false;
    }

    async searchOemInCollection(searchTerm, collectionHandle, signal) {
        this.currentSearchTerm = searchTerm;
        this.isLoading = true;
        this.showLoading();

        try {
            const seenHandles = new Set();
            let productsHTML = '';
            let matchCount = 0;

            await this.fetchAllCollectionProductCards(collectionHandle, {
                signal,
                onPage: (cards) => {
                    const filtered = this.filterCardsByOemDescription(cards, searchTerm, seenHandles);
                    if (!filtered.matchCount) return;

                    productsHTML += filtered.productsHTML;
                    matchCount = filtered.seenHandles.size;
                    this.renderOemSearchResults(
                        productsHTML,
                        matchCount,
                        `/collections/${collectionHandle}`
                    );
                },
            });

            if (signal?.aborted) return;

            if (matchCount === 0) {
                this.showNoProducts();
                this.isLoading = false;
                return;
            }

            this.currentCollectionHandle = collectionHandle;
        } catch (error) {
            if (error?.name === 'AbortError') return;
            xrefRollbar('searchOemInCollection', error);
            this.showNoProducts();
            this.isLoading = false;
        }
    }

    async searchOemAcrossManufacturers(searchTerm, signal) {
        const handles = this.getManufacturerHandles();

        if (!handles.length) {
            this.showNoProducts();
            this.isLoading = false;
            return;
        }

        try {
            const seenHandles = new Set();
            let productsHTML = '';
            let matchCount = 0;
            let hasRendered = false;

            const renderMatches = (cards) => {
                const filtered = this.filterCardsByOemDescription(cards, searchTerm, seenHandles);
                if (!filtered.matchCount) return;

                productsHTML += filtered.productsHTML;
                matchCount = filtered.seenHandles.size;
                hasRendered = true;
                this.renderOemSearchResults(productsHTML, matchCount);
            };

            await Promise.all(
                handles.map(async (handle) => {
                    if (signal?.aborted) return;

                    await this.fetchAllCollectionProductCards(handle, {
                        signal,
                        onPage: renderMatches,
                    });
                })
            );

            if (signal?.aborted) return;

            if (matchCount === 0) {
                this.showNoProducts();
                this.isLoading = false;
                return;
            }

            if (!hasRendered) {
                this.showNoProducts();
                this.isLoading = false;
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            xrefRollbar('searchOemAcrossManufacturers', error);
            this.showNoProducts();
            this.isLoading = false;
        }
    }

    async loadCollectionProducts(collectionHandle) {
        if (!collectionHandle || !this.sectionId) {
            return;
        }

        if (this.isLoading) {
            return;
        }

        if (this.currentCollectionHandle === collectionHandle && !this.productsListing.classList.contains('d-none')) {
            return;
        }

        // Clear the current search term when loading collection products
        this.currentSearchTerm = '';

        this.isLoading = true;
        this.currentCollectionHandle = collectionHandle;
        this.showLoading();

        const collectionUrl = `/collections/${collectionHandle}?view=filtercross`;
        await this.loadCollectionProductsFromPage(collectionUrl);
        this.isLoading = false;
    }

    async loadCollectionProductsFromPage(collectionUrl) {
        try {
            const url = new URL(collectionUrl, window.location.origin);
            url.searchParams.set('limit', '48');
            url.searchParams.set('view', 'filtercross');
            let response = await fetch(url.toString());
            if (response.status === 429) {
                // Rate limited: wait briefly (honouring Retry-After, max 5s) and retry once.
                const retryAfter = parseInt(response.headers.get('Retry-After'), 10);
                const delay = Math.min((retryAfter > 0 ? retryAfter : 2) * 1000, 5000);
                await new Promise((resolve) => setTimeout(resolve, delay));
                response = await fetch(url.toString());
            }
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            let productCards = [];
            productCards = doc.querySelectorAll('[data-product-card]');
            
            if (productCards.length === 0) {
                const productGrid = doc.querySelector('[data-products-grid], .product-list__grid, #product-grids');
                if (productGrid) {
                    productCards = productGrid.querySelectorAll('[data-product-card], .product-card, article');
                }
            }
            
            if (productCards.length === 0) {
                const collectionSection = doc.querySelector('[data-section-type="template-collection"], .collection-template');
                if (collectionSection) {
                    productCards = collectionSection.querySelectorAll('[data-product-card], .product-card, article[data-product-id]');
                }
            }
            
            if (productCards.length === 0) {
                const productLinks = doc.querySelectorAll('a[href*="/products/"]');
                productLinks.forEach(link => {
                    const productCard = link.closest('[data-product-card], .product-card, article, .col-lg-3, .col-md-4');
                    if (productCard && !productCards.includes(productCard)) {
                        productCards.push(productCard);
                    }
                });
            }

            if (productCards.length === 0) {
                this.showNoProducts();
                return;
            }

            let productsHTML = '';
            productCards.forEach((card) => {
                let cardHTML = card.outerHTML;
                const colWrapper = card.closest('[data-cross-reference-product-wrapper]');
                const productDescription = this.getProductDescriptionHtml(card) ||
                                         card.getAttribute('data-product-description') ||
                                         '';
                
                if (colWrapper) {
                    const wrapperClone = colWrapper.cloneNode(true);
                    if (productDescription && !wrapperClone.hasAttribute('data-product-description')) {
                        wrapperClone.setAttribute('data-product-description', productDescription);
                    }
                    productsHTML += wrapperClone.outerHTML;
                } else {
                    const wrapperAttrs = productDescription ? `data-product-description="${productDescription}"` : '';
                    productsHTML += `<div class="col-lg-3 col-md-4 col-6 px-md-3 px-2 d-flex align-items-stretch justify-content-around" data-cross-reference-product-wrapper ${wrapperAttrs}>${cardHTML}</div>`;
                }
            });

            const paginationHTML = this.extractPaginationFromPage(doc);
            let totalCount = productCards.length;
            
            // First, try to extract from the title element (contains collection.products_count)
            const titleElement = doc.querySelector('[data-cross-reference-title]');
            if (titleElement) {
                const titleText = titleElement.textContent || titleElement.innerText;
                // Extract number from text like "Search Results (50)" or "Search Results (123)"
                const countMatch = titleText.match(/\((\d+)\)/);
                if (countMatch) {
                    totalCount = parseInt(countMatch[1], 10);
                }
            }
            
            // Fallback to other methods if title extraction didn't work
            if (totalCount === productCards.length) {
                const totalProductsElement = doc.querySelector('[data-totalProducts] .total-products, .total-products[data-totalProducts]');
                if (totalProductsElement) {
                    const countText = totalProductsElement.textContent || totalProductsElement.innerText;
                    // Extract number from text like "123 results found" or "123"
                    const countMatch = countText.match(/(\d+)/);
                    if (countMatch) {
                        totalCount = parseInt(countMatch[1], 10);
                    }
                } else {
                    // Fallback: try to find in sidebar-filters or topbar-filters
                    const fallbackElement = doc.querySelector('[data-totalProducts] p, .total-products p');
                    if (fallbackElement) {
                        const countText = fallbackElement.textContent || fallbackElement.innerText;
                        const countMatch = countText.match(/(\d+)/);
                        if (countMatch) {
                            totalCount = parseInt(countMatch[1], 10);
                        }
                    } else {
                        const collectionMeta = doc.querySelector('meta[property="product:count"], [data-collection-count]');
                        if (collectionMeta) {
                            const countValue = collectionMeta.getAttribute('content') || collectionMeta.getAttribute('data-collection-count');
                            if (countValue) {
                                const countMatch = countValue.match(/(\d+)/);
                                if (countMatch) {
                                    totalCount = parseInt(countMatch[1], 10);
                                }
                            }
                        }
                    }
                }
            }

            this.allProducts = this.parseProductsFromHTML(productsHTML);
            this.renderProducts(productsHTML, totalCount, collectionUrl, paginationHTML);
            this.resetDropdown();
            // Restore selected class after reset
            this.restoreSelectedCollection();

        } catch (error) {
            if (error?.name === 'AbortError') {
                this.isLoading = false;
                return;
            }
            xrefRollbar('loadCollectionProductsFromPage', error);
            this.showError();
            this.isLoading = false;
        }
    }

    extractPaginationFromPage(doc) {
        const paginationParent = doc.querySelector('[data-pagination-parent], [data-data-paginationParent]');
        if (paginationParent) {
            return paginationParent.outerHTML;
        }
        
        const paginationNav = doc.querySelector('nav .pagination, .pagination');
        if (paginationNav) {
            const navElement = paginationNav.closest('nav');
            if (navElement) {
                return navElement.outerHTML;
            }
            return paginationNav.outerHTML;
        }
        
        return '';
    }

    renderProducts(productsHTML, productCount, collectionUrl, paginationHTML = '') {
        this.paginationHTML = paginationHTML;
        
        if (!this.allProducts || this.allProducts.length === 0) {
            this.allProducts = this.parseProductsFromHTML(productsHTML);
        }

        const existingTitleElement = this.productsListing.querySelector('[data-cross-reference-title]');
        const existingViewAllElement = this.productsListing.querySelector('.view-all');
        const titleViewAllWrapper = this.productsListing.querySelector('[data-cross-reference-title-wrapper]') || 
                                   this.productsListing.querySelector('div:first-child');
        const productsGrid = this.productsListing.querySelector('.row');

        if (titleViewAllWrapper) {
            titleViewAllWrapper.classList.remove('d-none', 'd-block', 'd-inline', 'd-inline-block');
            titleViewAllWrapper.classList.add('d-flex');
        }

        if (existingTitleElement) {
            const titleText = existingTitleElement.textContent.split('(')[0].trim();
            existingTitleElement.textContent = `${titleText} (${productCount})`;
            existingTitleElement.classList.remove('d-none', 'd-flex', 'd-inline', 'd-inline-block');
            existingTitleElement.classList.add('d-block');
        }

        if (existingViewAllElement) {
            const clearLink = existingViewAllElement.querySelector('a');
            if (clearLink) {
                clearLink.href = collectionUrl || '#';
            }
            existingViewAllElement.classList.remove('d-none', 'd-flex', 'd-inline', 'd-inline-block');
            existingViewAllElement.classList.add('d-block');
        }

        if (productsGrid) {
            productsGrid.style.opacity = '0';
            productsGrid.style.transition = 'opacity 0.3s ease-in-out';
            productsGrid.innerHTML = productsHTML;
            this.fixLazyLoadedImages(productsGrid);
            
            setTimeout(() => {
                productsGrid.style.opacity = '1';
            }, 50);
        }

        this.productsListing.classList.remove('d-none');
        this.productsListing.classList.add('d-block');
        const notFoundsElement = document.querySelector('.not-founds');
        if (notFoundsElement) {
            notFoundsElement.classList.remove('d-block');
            notFoundsElement.classList.add('d-none');
        }

        // Always render pagination (will hide if no pagination HTML)
        this.renderPagination();

        this.reinitializeProductCards();
    }

    parseProductsFromHTML(productsHTML) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(productsHTML, 'text/html');
        const productElements = doc.querySelectorAll('[data-cross-reference-product-wrapper]');
        return Array.from(productElements).map(el => el.outerHTML);
    }

    renderPagination() {
        const paginationWrapper = this.productsListing.querySelector('.pagination-wrapper');
        
        if (!paginationWrapper) {
            return;
        }

        const paginationContainer = paginationWrapper.querySelector('[data-cross-reference-pagination-container]');
        if (!paginationContainer) {
            return;
        }

        // Clear previous pagination content
        paginationContainer.innerHTML = '';

        if (!this.paginationHTML || this.paginationHTML.trim() === '') {
            paginationWrapper.classList.add('d-none');
            return;
        }

        const parser = new DOMParser();
        const paginationDoc = parser.parseFromString(this.paginationHTML, 'text/html');
        let paginationElement = paginationDoc.querySelector('[data-pagination-parent], [data-data-paginationParent]');
        if (!paginationElement) {
            paginationElement = paginationDoc.querySelector('nav, .pagination');
        }

        if (paginationElement) {
            const clonedPagination = paginationElement.cloneNode(true);
            paginationContainer.appendChild(clonedPagination);
            paginationWrapper.classList.remove('d-none');
            this.attachPaginationListeners(clonedPagination);
        } else {
            paginationWrapper.classList.add('d-none');
        }
    }

    attachPaginationListeners(paginationElement) {
        const paginationLinks = paginationElement.querySelectorAll('a[data-pagelinks], a[href*="page="]');
        
        paginationLinks.forEach(link => {
            link.addEventListener('click', async (e) => {
                e.preventDefault();
                const href = link.getAttribute('href');
                if (href && href !== '#') {
                    const url = new URL(href, window.location.origin);
                    
                    if (url.pathname === '/search' || url.pathname.startsWith('/search')) {
                        const searchParams = new URLSearchParams(url.search);
                        const searchTerm = searchParams.get('q') || '';
                        const page = parseInt(searchParams.get('page') || '1');
                        
                        if (searchTerm) {
                            this.abortActiveSearch();
                            this.activeSearchAbort = new AbortController();
                            const signal = this.activeSearchAbort.signal;
                            const selectedCollectionHandle = this.getSelectedValue();

                            if (selectedCollectionHandle) {
                                await this.searchOemInCollection(searchTerm, selectedCollectionHandle, signal);
                            } else {
                                await this.searchOemAcrossManufacturers(searchTerm, signal);
                            }
                        }
                    } else {
                        await this.loadCollectionProductsFromPage(url.pathname + url.search);
                    }
                }
            });
        });
    }

    showNoProducts() {
        this.productsListing.classList.remove('d-block');
        this.productsListing.classList.add('d-none');
        const notFoundsElement = document.querySelector('.not-founds');
        if (notFoundsElement) {
            notFoundsElement.classList.remove('d-none');
            notFoundsElement.classList.add('d-block');
            
            // Update the search term display
            const searchTermDisplay = notFoundsElement.querySelector('[data-search-term-display]');
            if (searchTermDisplay) {
                searchTermDisplay.textContent = this.currentSearchTerm || '';
            }
        }
        this.resetDropdown();
    }

    clearSearch() {
        this.abortActiveSearch();
        this.searchRequestId++;

        const searchInput = document.getElementById('serial-number-input');
        if (searchInput) {
            searchInput.value = '';
        }
        
        // Clear the current search term
        this.currentSearchTerm = '';
        
        // Remove selected class from all collection options
        if (this.selectElement) {
            const allOptions = this.selectElement.querySelectorAll('li[data-value]');
            allOptions.forEach(option => {
                option.classList.remove('selected');
            });
        }
        
        this.allProducts = [];
        this.paginationHTML = '';
        const paginationWrapper = this.productsListing?.querySelector('.pagination-wrapper');
        if (paginationWrapper) {
            paginationWrapper.classList.add('d-none');
        }
        
        if (this.productsListing) {
            this.productsListing.classList.remove('d-block');
            this.productsListing.classList.add('d-none');
            
            const productsGrid = this.productsListing.querySelector('.row');
            if (productsGrid) {
                productsGrid.innerHTML = '';
            }
            
            const titleElement = this.productsListing.querySelector('[data-cross-reference-title]');
            if (titleElement) {
                const titleText = titleElement.textContent.split('(')[0].trim();
                titleElement.textContent = `${titleText} (0)`;
                titleElement.classList.remove('d-block');
                titleElement.classList.add('d-none');
            }
            
            const viewAllElement = this.productsListing.querySelector('.view-all');
            if (viewAllElement) {
                viewAllElement.classList.remove('d-block');
                viewAllElement.classList.add('d-none');
            }
            
            const titleViewAllWrapper = this.productsListing.querySelector('[data-cross-reference-title-wrapper]') || 
                                       this.productsListing.querySelector('div:first-child');
            if (titleViewAllWrapper) {
                titleViewAllWrapper.classList.remove('d-flex', 'd-block');
                titleViewAllWrapper.classList.add('d-none');
            }
        }
        
        // Hide not-founds message
        const notFoundsElement = document.querySelector('.not-founds');
        if (notFoundsElement) {
            notFoundsElement.classList.remove('d-block');
            notFoundsElement.classList.add('d-none');
        }
        
        if (this.selectElement) {
            const toggleButton = this.selectElement.querySelector('button[data-toggle="options"]');
            const optionText = this.selectElement.querySelector('.option_txt');
            if (toggleButton && optionText) {
                optionText.textContent = 'Select Manufacturer';
            }
            
            if (this.selectElement.classList.contains('open')) {
                this.selectElement.closeDropdown();
            }
            
            const options = this.selectElement.querySelectorAll('li');
            options.forEach(option => {
                option.classList.remove('selected');
            });
            
            const radioButtons = this.selectElement.querySelectorAll('input[type="radio"]');
            radioButtons.forEach(radio => {
                radio.checked = false;
            });
        }
        
        this.currentCollectionHandle = null;
        this.isLoading = false;
    }

    async performPredictiveSearch() {
        const searchInput = document.getElementById('serial-number-input');
        
        if (!searchInput) {
            return;
        }

        const searchTerm = searchInput.value.trim();
        
        if (!searchTerm || searchTerm.length === 0) {
            return;
        }

        this.abortActiveSearch();
        this.activeSearchAbort = new AbortController();
        const signal = this.activeSearchAbort.signal;
        const requestId = ++this.searchRequestId;

        this.currentSearchTerm = searchTerm;

        if (this.selectElement) {
            const allOptions = this.selectElement.querySelectorAll('li[data-value]');
            allOptions.forEach(option => {
                option.classList.remove('selected');
                const radio = option.querySelector('input[type="radio"]');
                if (radio) {
                    radio.checked = false;
                }
            });
            
            const toggleButton = this.selectElement.querySelector('button[data-toggle="options"]');
            const optionText = this.selectElement.querySelector('.option_txt');
            if (toggleButton && optionText) {
                optionText.textContent = 'Select Manufacturer';
            }
        }
        
        this.currentCollectionHandle = null;
        this.isLoading = true;
        this.showLoading();

        try {
            const shopifyResults = await this.tryShopifyOemSearch(searchTerm, signal);
            if (signal.aborted || requestId !== this.searchRequestId) return;

            if (shopifyResults?.matchCount > 0) {
                this.renderOemSearchResults(shopifyResults.productsHTML, shopifyResults.matchCount);
                return;
            }

            const selectedCollectionHandle = this.getSelectedValue();

            if (selectedCollectionHandle) {
                await this.searchOemInCollection(searchTerm, selectedCollectionHandle, signal);
            } else {
                await this.searchOemAcrossManufacturers(searchTerm, signal);
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            xrefRollbar('performPredictiveSearch', error);
            this.showError();
            this.isLoading = false;
        }
    }

    async searchAllProducts(searchTerm, page = 1) {
        if (this.isLoading) {
            return;
        }
        
        // Store the current search term
        this.currentSearchTerm = searchTerm;
        
        this.isLoading = true;
        this.showLoading();

        try {
            const searchUrl = `/search?q=${encodeURIComponent(searchTerm)}&view=cross-reference&limit=48${page > 1 ? `&page=${page}` : ''}`;
            
            const response = await fetch(searchUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const searchSection = doc.querySelector('#template-search');
            let productCards = [];
            
            if (searchSection) {
                const allDirectChildren = Array.from(searchSection.children);
                productCards = allDirectChildren.filter(card => {
                    const isProductWrapper = card.hasAttribute('data-cross-reference-product-wrapper');
                    const hasProductLink = card.querySelector('a[href*="/products/"]');
                    const notPagination = !card.closest('[data-pagination-parent]') && 
                                          !card.closest('.pagination-wrapper') &&
                                          !card.closest('[data-data-paginationParent]') &&
                                          !card.hasAttribute('data-data-paginationParent') &&
                                          !card.hasAttribute('data-pagination-parent');
                    return isProductWrapper && hasProductLink && notPagination;
                });
            }
            
            if (productCards.length === 0) {
                await this.searchOemAcrossManufacturers(searchTerm);
                return;
            }

            let productsHTML = '';
            productCards.forEach((card) => {
                let productDescription = card.getAttribute('data-product-description') || 
                                       card.querySelector('[data-product-description]')?.getAttribute('data-product-description') || '';
                const cardClone = card.cloneNode(true);
                cardClone.classList.remove('col-lg-4', 'col-lg-6', 'col-lg-2');
                cardClone.classList.add('col-lg-3');
                
                if (productDescription) {
                    cardClone.setAttribute('data-product-description', productDescription);
                }
                
                productsHTML += cardClone.outerHTML;
            });

            // Extract pagination from search page
            const paginationHTML = this.extractPaginationFromPage(doc);

            // Extract total search results count from the page
            let totalCount = productCards.length; // Default to page count if total not found
            
            // First, try to extract from the title element (contains search.results_count or collection.products_count)
            const titleElement = doc.querySelector('[data-cross-reference-title]');
            if (titleElement) {
                const titleText = titleElement.textContent || titleElement.innerText;
                // Extract number from text like "Search Results (50)" or "Search Results (123)"
                const countMatch = titleText.match(/\((\d+)\)/);
                if (countMatch) {
                    totalCount = parseInt(countMatch[1], 10);
                }
            }
            
            // Fallback to other methods if title extraction didn't work
            if (totalCount === productCards.length) {
                const totalProductsElement = doc.querySelector('[data-totalProducts] .total-products, .total-products[data-totalProducts]');
                if (totalProductsElement) {
                    const countText = totalProductsElement.textContent || totalProductsElement.innerText;
                    // Extract number from text like "123 results found" or "123"
                    const countMatch = countText.match(/(\d+)/);
                    if (countMatch) {
                        totalCount = parseInt(countMatch[1], 10);
                    }
                } else {
                    // Fallback: try to find in sidebar-filters or topbar-filters
                    const fallbackElement = doc.querySelector('[data-totalProducts] p, .total-products p');
                    if (fallbackElement) {
                        const countText = fallbackElement.textContent || fallbackElement.innerText;
                        const countMatch = countText.match(/(\d+)/);
                        if (countMatch) {
                            totalCount = parseInt(countMatch[1], 10);
                        }
                    }
                }
            }

            this.allProducts = this.parseProductsFromHTML(productsHTML);
            this.renderProducts(productsHTML, totalCount, '', paginationHTML);
            this.isLoading = false;

        } catch (error) {
            xrefRollbar('searchAllProducts', error);
            this.showError();
            this.isLoading = false;
        }
    }

    async searchInCollection(searchTerm, collectionHandle, page = 1, restoreSelection = true) {
        if (this.isLoading) {
            return;
        }
        
        // Store the current search term
        this.currentSearchTerm = searchTerm;
        
        this.isLoading = true;
        this.showLoading();
        const collectionUrl = `/collections/${collectionHandle}`;

        try {
            const searchUrl = `/search?q=${encodeURIComponent(searchTerm)}&view=cross-reference&options[prefix]=last&limit=48${page > 1 ? `&page=${page}` : ''}`;
            
            const response = await fetch(searchUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const searchSection = doc.querySelector('#template-search');
            let productCards = [];
            
            if (searchSection) {
                const allDirectChildren = Array.from(searchSection.children);
                productCards = allDirectChildren.filter(card => {
                    const isProductWrapper = card.hasAttribute('data-cross-reference-product-wrapper');
                    const hasProductLink = card.querySelector('a[href*="/products/"]');
                    const notPagination = !card.closest('[data-pagination-parent]') && 
                                          !card.closest('.pagination-wrapper') &&
                                          !card.closest('[data-data-paginationParent]') &&
                                          !card.hasAttribute('data-data-paginationParent') &&
                                          !card.hasAttribute('data-pagination-parent');
                    return isProductWrapper && hasProductLink && notPagination;
                });
            }
            
            if (productCards.length === 0) {
                await this.loadCollectionProductsFromPage(collectionUrl);
                if (this.productsListing && !this.productsListing.classList.contains('d-none')) {
                    this.filterProductsBySearchTerm(searchTerm);
                }
                this.isLoading = false;
                return;
            }

            let productsHTML = '';
            productCards.forEach((card) => {
                let productDescription = card.getAttribute('data-product-description') || 
                                       card.querySelector('[data-product-description]')?.getAttribute('data-product-description') || '';
                const cardClone = card.cloneNode(true);
                cardClone.classList.remove('col-lg-4', 'col-lg-6', 'col-lg-2');
                cardClone.classList.add('col-lg-3');
                
                if (productDescription) {
                    cardClone.setAttribute('data-product-description', productDescription);
                }
                
                productsHTML += cardClone.outerHTML;
            });

            // Extract pagination from search page
            const paginationHTML = this.extractPaginationFromPage(doc);

            // Extract total search results count from the page
            let totalCount = productCards.length; // Default to page count if total not found
            
            // First, try to extract from the title element (contains search.results_count or collection.products_count)
            const titleElement = doc.querySelector('[data-cross-reference-title]');
            if (titleElement) {
                const titleText = titleElement.textContent || titleElement.innerText;
                // Extract number from text like "Search Results (50)" or "Search Results (123)"
                const countMatch = titleText.match(/\((\d+)\)/);
                if (countMatch) {
                    totalCount = parseInt(countMatch[1], 10);
                }
            }
            
            // Fallback to other methods if title extraction didn't work
            if (totalCount === productCards.length) {
                const totalProductsElement = doc.querySelector('[data-totalProducts] .total-products, .total-products[data-totalProducts]');
                if (totalProductsElement) {
                    const countText = totalProductsElement.textContent || totalProductsElement.innerText;
                    // Extract number from text like "123 results found" or "123"
                    const countMatch = countText.match(/(\d+)/);
                    if (countMatch) {
                        totalCount = parseInt(countMatch[1], 10);
                    }
                } else {
                    // Fallback: try to find in sidebar-filters or topbar-filters
                    const fallbackElement = doc.querySelector('[data-totalProducts] p, .total-products p');
                    if (fallbackElement) {
                        const countText = fallbackElement.textContent || fallbackElement.innerText;
                        const countMatch = countText.match(/(\d+)/);
                        if (countMatch) {
                            totalCount = parseInt(countMatch[1], 10);
                        }
                    }
                }
            }

            this.allProducts = this.parseProductsFromHTML(productsHTML);
            this.renderProducts(productsHTML, totalCount, collectionUrl, paginationHTML);
            this.resetDropdown();
            // Restore selected class after reset (only if restoreSelection is true)
            if (restoreSelection) {
                this.restoreSelectedCollection();
            }
            this.isLoading = false;

        } catch (error) {
            xrefRollbar('searchInCollection', error);
            this.showError();
            this.isLoading = false;
        }
    }

    resetDropdown() {
        if (this.selectElement) {
            const toggleButton = this.selectElement.querySelector('button[data-toggle="options"]');
            const optionText = this.selectElement.querySelector('.option_txt');
            if (toggleButton && optionText) {
                optionText.textContent = 'Select Manufacturer';
            }
            
            if (this.selectElement.classList.contains('open')) {
                this.selectElement.closeDropdown();
            }
            
            const options = this.selectElement.querySelectorAll('li');
            options.forEach(option => {
                option.classList.remove('selected');
            });
            
            const radioButtons = this.selectElement.querySelectorAll('input[type="radio"]');
            radioButtons.forEach(radio => {
                radio.checked = false;
            });
        }
    }

    restoreSelectedCollection() {
        // Restore selected class to the current collection option after results are rendered
        if (this.currentCollectionHandle && this.selectElement) {
            const selectedOption = this.selectElement.querySelector(`li[data-value="${this.currentCollectionHandle}"]`);
            if (selectedOption) {
                selectedOption.classList.add('selected');
                const radio = selectedOption.querySelector('input[type="radio"]');
                if (radio) {
                    radio.checked = true;
                }
                
                // Update toggle button text to show selected manufacturer name
                const toggleButton = this.selectElement.querySelector('button[data-toggle="options"]');
                const optionText = this.selectElement.querySelector('.option_txt');
                const label = selectedOption.querySelector('label');
                
                if (toggleButton && optionText && label) {
                    optionText.textContent = label.textContent.trim();
                }
            }
        }
    }

    filterProductsBySearchTerm(searchTerm) {
        const productsGrid = this.productsListing.querySelector('.row');
        if (!productsGrid) return;

        const productCards = productsGrid.querySelectorAll('[data-product-card], [data-cross-reference-product-wrapper]');
        
        let visibleCount = 0;
        productCards.forEach((card) => {
            const wrapper = card.matches('[data-cross-reference-product-wrapper]') ? card : card.closest('[data-cross-reference-product-wrapper]');
            const cardElement = card.matches('[data-product-card]') ? card : card.querySelector('[data-product-card]') || card;
            const target = wrapper || card;
            const matches = this.productMatchesOemDescriptionSearch(cardElement, searchTerm);

            if (matches) {
                target.classList.remove('d-none');
                visibleCount++;
            } else {
                target.classList.add('d-none');
            }
        });

        const titleElement = this.productsListing.querySelector('[data-cross-reference-title]');
        if (titleElement) {
            const titleText = titleElement.textContent.split('(')[0].trim();
            titleElement.textContent = `${titleText} (${visibleCount})`;
        }

        if (visibleCount === 0) {
            this.showNoProducts();
        } else {
            const notFoundsElement = document.querySelector('.not-founds');
            if (notFoundsElement) {
                notFoundsElement.classList.remove('d-block');
                notFoundsElement.classList.add('d-none');
            }
            this.processOemMatches();
        }
    }

    showLoading() {
        if (this.productsListing) {
            this.productsListing.classList.remove('d-none');
            this.productsListing.classList.add('d-block');
            const notFoundsElement = document.querySelector('.not-founds');
            if (notFoundsElement) {
                notFoundsElement.classList.remove('d-block');
                notFoundsElement.classList.add('d-none');
            }
            
            const titleElement = this.productsListing.querySelector('[data-cross-reference-title]');
            const viewAllElement = this.productsListing.querySelector('.view-all');
            const titleViewAllWrapper = this.productsListing.querySelector('[data-cross-reference-title-wrapper]') || 
                                       this.productsListing.querySelector('div:first-child');
            if (titleElement) {
                titleElement.classList.remove('d-block');
                titleElement.classList.add('d-none');
            }
            if (viewAllElement) {
                viewAllElement.classList.remove('d-block');
                viewAllElement.classList.add('d-none');
            }
            if (titleViewAllWrapper) {
                titleViewAllWrapper.classList.remove('d-flex', 'd-block');
                titleViewAllWrapper.classList.add('d-none');
            }
            
            const productsGrid = this.productsListing.querySelector('.row');
            if (productsGrid) {
                productsGrid.innerHTML = '<div class="col-12 text-center py-5" style="min-height: 200px; display: flex; align-items: center; justify-content: center;"><div class="spinner-border text-primary" role="status" style="display: inline-block; width: 3rem; height: 3rem; border-width: 0.3em; border-color: currentColor; border-right-color: transparent; border-radius: 50%; animation: spinner-border 0.75s linear infinite;"><span class="visually-hidden">Loading products...</span></div></div>';
            }
        }
    }

    showError() {
        if (this.productsListing) {
            const titleElement = this.productsListing.querySelector('[data-cross-reference-title]');
            const viewAllElement = this.productsListing.querySelector('.view-all');
            const titleViewAllWrapper = this.productsListing.querySelector('[data-cross-reference-title-wrapper]') || 
                                       this.productsListing.querySelector('div:first-child');
            if (titleViewAllWrapper) {
                titleViewAllWrapper.classList.remove('d-none');
                titleViewAllWrapper.classList.add('d-flex');
            }
            if (titleElement) {
                titleElement.classList.remove('d-none');
                titleElement.classList.add('d-block');
            }
            if (viewAllElement) {
                viewAllElement.classList.remove('d-none');
                viewAllElement.classList.add('d-block');
            }
            
            const productsGrid = this.productsListing.querySelector('.row');
            if (productsGrid) {
                productsGrid.innerHTML = '<div class="col-12 text-center py-5"><p class="text-danger">Error loading products. Please try again.</p></div>';
            }
        }
    }

    fixLazyLoadedImages(container) {
        if (!container) return;
        
        if (typeof lozad !== 'undefined') {
            const lazyImages = container.querySelectorAll('.lozad, [data-src], [data-srcset]');
            const observer = lozad();
            lazyImages.forEach(img => {
                observer.triggerLoad(img);
            });
        } else {
            const images = container.querySelectorAll('img[data-src]');
            images.forEach(img => {
                if (img.hasAttribute('data-src') && !img.src) {
                    img.src = img.getAttribute('data-src');
                    img.removeAttribute('data-src');
                }
                if (img.hasAttribute('data-srcset') && !img.srcset) {
                    img.srcset = img.getAttribute('data-srcset');
                    img.removeAttribute('data-srcset');
                }
                if (img.classList.contains('lozad')) {
                    img.classList.remove('lozad');
                }
            });
        }
    }

    reinitializeProductCards() {
        const productCards = this.productsListing.querySelectorAll('[data-product-card]');
        if (productCards.length > 0) {
            // Set ship date for each product card
            productCards.forEach(card => {
                this.setShipDateForCard(card);
            });
            
            // Hide shipping info for out of stock products (run after ship date is set)
            setTimeout(() => {
                productCards.forEach(card => {
                    this.hideShippingInfoForOutOfStock(card);
                });
            }, 100);
            
            window.dispatchEvent(new CustomEvent('products-loaded', {
                detail: { products: productCards }
            }));

            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.processOemMatches();
                }, 0);
            });
        }
    }

    hideShippingInfoForOutOfStock(card) {
        // Hide shipping info when inventory is 0 on cross-reference page
        const inventoryInfo = card.querySelector('.inventory-info');
        const shippingInfo = card.querySelector('.product__shipping-info');
        const productInventory = card.querySelector('.product__inventory');
        
        if (!inventoryInfo || !shippingInfo || !productInventory) {
            return;
        }
        
        // Check if inventory is 0 (out of stock)
        const dangerSpan = inventoryInfo.querySelector('.text-danger');
        const inventoryText = inventoryInfo.textContent.trim();
        const hasZeroInventory = /^0\s+/.test(inventoryText) || inventoryText.startsWith('0');
        const isOutOfStock = dangerSpan !== null || hasZeroInventory;
        
        // Hide shipping info if out of stock
        if (isOutOfStock) {
            productInventory.classList.add('out-of-stock');
            shippingInfo.style.display = 'none';
        }
    }

    setShipDateForCard(card) {
        const inventoryWrapper = card.querySelector('.product-inventory-wrapper');
        if (!inventoryWrapper) return;

        const expectedDateElement = inventoryWrapper.querySelector('[data-expected-date]');
        if (!expectedDateElement) return;

        const shipDateInput = card.querySelector('[data-shipdate-input]');
        const isExpectedDate = expectedDateElement.getAttribute('data-expected-date') === 'true';
        const targetDate = Utility.resolveShipDate({
            inputDate: expectedDateElement.getAttribute('data-inputdate') || '',
            isExpectedDate,
            businessDays: expectedDateElement.getAttribute('data-businessdays'),
            holidays: expectedDateElement.getAttribute('data-holidays'),
            checkavail: expectedDateElement.getAttribute('data-checkavail'),
        });

        if (isExpectedDate) {
            expectedDateElement.innerHTML = `Expected Ship Date: <span class="fw-normal">${targetDate}</span>`;
        } else if (
            Utility.shouldShowShipDateInsteadOfMessage({
                inputDate: expectedDateElement.getAttribute('data-inputdate') || '',
            })
        ) {
            const dateMarkup = `<span class="fw-normal">${targetDate}</span>`;
            expectedDateElement.innerHTML = Utility.isShipTomorrowWeekend()
                ? `Expected Ship Date: ${dateMarkup}`
                : dateMarkup;
        }

        if (shipDateInput && targetDate) {
            shipDateInput.value = targetDate;
        }
    }

    processOemMatches() {
        let productItems = Array.from(this.productsListing.querySelectorAll('[data-product-card]'));
        
        if (productItems.length === 0) {
            productItems = Array.from(this.productsListing.querySelectorAll('[data-product-description]'));
        }
        
        if (productItems.length === 0) {
            const allCards = Array.from(this.productsListing.querySelectorAll('[data-cross-reference-product-wrapper], [data-product-card]'));
            productItems = allCards.filter(card => {
                return card.querySelector('[data-manufacturer], [data-oem], .search-oem');
            });
        }

        const searchTerm = (this.currentSearchTerm || '').trim();
        
        productItems.forEach((item) => {
            const cardElement = item.matches('[data-product-card]') ? item : item.querySelector('[data-product-card]') || item;
            const descriptionHtml = this.getProductDescriptionHtml(cardElement);
            const searchOem = item.querySelector('.search-oem') || cardElement.querySelector('.search-oem');
            const manufacturerSpan = item.querySelector('[data-manufacturer]') || cardElement.querySelector('[data-manufacturer]');
            const oemSpan = item.querySelector('[data-oem]') || cardElement.querySelector('[data-oem]');
            
            if (!descriptionHtml || !manufacturerSpan || !oemSpan) {
                if (searchOem) searchOem.classList.add('d-none');
                return;
            }

            const match = searchTerm
                ? this.findOemTableRow(descriptionHtml, searchTerm)
                : this.findOemTableRow(descriptionHtml, '');

            if (!match) {
                if (searchOem) searchOem.classList.add('d-none');
                return;
            }

            manufacturerSpan.textContent = match.manufacturer;
            oemSpan.textContent = match.oem;

            if (searchOem) searchOem.classList.remove('d-none');

            const labelDescInput = item.querySelector('[data-labeldesc-input]') || cardElement.querySelector('[data-labeldesc-input]');
            if (labelDescInput && match.oem && match.manufacturer) {
                labelDescInput.value = `${match.oem} ^ ${match.manufacturer}`;
            }
        });
    }
}

wrapCrossReferencePrototype(CrossReferencePage, 'CrossReferencePage');

try {
  customElements.define('cross-reference', CrossReferencePage);
} catch (e) {
  xrefRollbar('bootstrap.define', e);
}